import { useSearchParams } from 'next/navigation';

import { useRetainedValue } from '~hooks/useRetainedValue';
import { usePathname, useRouter } from '~i18n/navigation';
import { EndlessCarousel } from '~shared/ui/molecules/EndlessCarousel';
import { updateQueryParamsWithPathname } from '~utils/searchParams';
import { ScrollWithSelected } from '~widgets/BotWizard/ui/ScrollWithSelected';

import { isValueSliders } from '../../model/guards';
import type {
	BotWizardFieldNames,
	BotWizardFieldSliders,
	BotWizardValues,
} from '../../model/types';
import {
	convertSliderValueToQuery,
	getDependencyContent,
} from '../../model/utils';
import { DeferredSlider } from './DeferredSlider';
import {
	Description,
	desktopHidden,
	mobileHidden,
	SliderLabel,
	Sliders,
	SliderWrapper,
	StyledCardButton,
	StyledFieldSliders,
} from './styles';

export const FieldSliders = ({
	values,
	...field
}: BotWizardFieldSliders & {
	id: BotWizardFieldNames;
	values: BotWizardValues;
}) => {
	const dependencyPresets =
		getDependencyContent({
			data: field.data.presets,
			contentDependency: field.contentDependency,
			values,
		}) ?? [];
	const data = field.data;
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const getValue = () => {
		if (isValueSliders(field, values, field.id)) {
			return values[field.id];
		}
		return undefined;
	};

	const [value, setValue] = useRetainedValue(getValue()?.preset, (v) => {
		router.push(
			updateQueryParamsWithPathname(
				{ [field.queryKey]: v },
				pathname,
				searchParams
			)
		);
	});

	const getPreset = () => {
		return dependencyPresets.find((el) => el.id === value);
	};

	const selectedPreset = dependencyPresets.find(
		(el) => el.id === getValue()?.preset
	);

	return (
		<StyledFieldSliders>
			<ScrollWithSelected
				className={desktopHidden}
				selected={(item) => item.id === value}
				array={dependencyPresets.map((el) => ({ ...el, key: el.id }))}
				template={(item, selected) => (
					<StyledCardButton
						selected={selected}
						width={300}
						height={300}
						priority={false}
						quality={90}
						aspectRatio="vertical"
						image={item.image}
						item={item.media}
						text={item.name}
						onClick={() => setValue(item.id)}
					/>
				)}
			/>
			<EndlessCarousel
				className={mobileHidden}
				selected={(item) => item.id === value}
				array={dependencyPresets.map((el) => ({ ...el, key: el.id }))}
				onChange={(item) => setValue(item.id)}
				template={(item, selected) => (
					<StyledCardButton
						selected={selected}
						width={300}
						height={300}
						priority={false}
						quality={90}
						aspectRatio="vertical"
						image={item.image}
						item={item.media}
						text={item.name}
					/>
				)}
			/>
			<Description size="S">{selectedPreset?.description || '\t'}</Description>
			<Sliders>
				{data.sliders.map((slider, index) => (
					<SliderWrapper key={slider.id}>
						<SliderLabel align="right">{slider.min}</SliderLabel>
						<DeferredSlider
							onChange={(value) => {
								router.push(
									updateQueryParamsWithPathname(
										getPreset()?.custom
											? { [field.queryKey + slider.queryKey]: value }
											: convertSliderValueToQuery(
													{
														preset:
															dependencyPresets.find((el) => el.custom)?.id ||
															'',
														values: Object.fromEntries(
															data.sliders.map((el, i) => [
																el.id,
																slider.id === el.id
																	? value.toString()
																	: getPreset()?.values[i].toString() || '0',
															])
														),
													},
													field
											  ),
										pathname,
										searchParams
									),
									{ scroll: false }
								);
							}}
							value={
								(getPreset()?.custom
									? parseInt(getValue()?.values[slider.id] || '0')
									: getPreset()?.values[index]) || 0
							}
							min={0}
							max={100}
						/>
						<SliderLabel align="left">{slider.max}</SliderLabel>
					</SliderWrapper>
				))}
			</Sliders>
		</StyledFieldSliders>
	);
};
